// Pure mapping between the in-memory Row (flat 99-column object plus internal
// bookkeeping) and the PocketBase `records` collection shape. No SDK import
// here so this stays trivially unit-testable.

import type { Row } from "../util";
import { DB_COLUMNS } from "../schema";

/** Shape of a PocketBase `records` row as returned by the SDK. */
export interface RecordModel {
  id: string;
  patient_id: string;
  lesion_id: string;
  data: Record<string, string>;
  synthetic?: boolean;
  owner?: string;
  updated?: string;
}

/** Payload written to PocketBase when creating/updating a record. */
export interface RecordPayload {
  patient_id: string;
  lesion_id: string;
  data: Record<string, string>;
  synthetic: boolean;
  owner: string;
}

/** Extract just the 99 schema columns from a Row (drop internal `_` fields). */
export function rowToData(row: Row): Record<string, string> {
  const data: Record<string, string> = {};
  DB_COLUMNS.forEach((col) => {
    data[col] = row[col] ?? "";
  });
  return data;
}

/** Build the PocketBase write payload for a row, attributing it to `ownerId`. */
export function rowToPayload(row: Row, ownerId: string): RecordPayload {
  return {
    patient_id: row.Patient_ID ?? "",
    lesion_id: row.Lesion_ID ?? "",
    data: rowToData(row),
    synthetic: Boolean(row._synthetic),
    owner: row._owner || ownerId,
  };
}

/** Convert a PocketBase record into an in-memory Row, carrying its id/owner. */
export function recordToRow(record: RecordModel): Row {
  const row: Row = {};
  DB_COLUMNS.forEach((col) => {
    row[col] = record.data?.[col] ?? "";
  });
  // Keep the original keys authoritative even if absent from `data`.
  row.Patient_ID = record.patient_id ?? row.Patient_ID ?? "";
  row.Lesion_ID = record.lesion_id ?? row.Lesion_ID ?? "";
  row._pb_id = record.id;
  row._owner = record.owner ?? "";
  row._synthetic = Boolean(record.synthetic);
  if (record.updated) row._saved_at = record.updated;
  return row;
}
